import get from "lodash/get";
import { useMemo } from "react";

import { CountTotalResult } from "@/lib/statistics-analytic-utils";
import { Currency } from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/util";
import { HeroResultsDto } from "@/shared/api";

export const fmt = (n: number | string) => {
  if (typeof n === "string") {
    return n;
  }

  if (isNaN(n)) {
    return 0;
  }

  if (Math.abs(n) > 1000) {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n / 1000) + "K";
  }

  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(n);
};

export const useSummaryRows = (
  heroTotal: HeroResultsDto,
  opponentTotal: CountTotalResult,
  currency: Currency
) => {
  const [currencyKeyLower, currencyKeyUpper] = useMemo(() => {
    if (currency === "Usd") {
      return ["_usd", "Usd"];
    }
    if (currency === "BB") {
      return ["_bb", "BB"];
    }

    return ["", ""];
  }, [currency]);

  const getKeyUpper = (key: string) => {
    return `${key}${currencyKeyUpper}` as keyof typeof opponentTotal;
  };

  const getKeyLower = (key: string) => {
    return `${key}${currencyKeyLower}` as keyof typeof heroTotal;
  };

  const getHeroValue = (key: string) => {
    return heroTotal[getKeyLower(key)];
  };

  const getOpponentValue = (key: string) => {
    return opponentTotal[getKeyUpper(key)] as number;
  };

  const getCategorizedValue = (type: string, key: string, withCurrency?: boolean) => {
    return get(
      opponentTotal.categorized,
      `${type}_${key}${withCurrency ? currencyKeyLower : withCurrency === false ? "_bb" : ""}`
    ) as unknown as number;
  };

  const rows = [
    {
      label: "Unraked Winrate",
      data: [
        "",
        (heroTotal.hero_gross_profit_bb / heroTotal.hero_hands_count) * 100,
        (opponentTotal.loserGrossProfitBB / opponentTotal.loserHands) * 100,
        (opponentTotal.winnerGrossProfitBB / opponentTotal.winnerHands) * 100,
        "",
        (getCategorizedValue("weak_loser", "gross_profit", false) /
          getCategorizedValue("weak_loser", "hands")) *
          100,
        (getCategorizedValue("middle_loser", "gross_profit", false) /
          getCategorizedValue("middle_loser", "hands")) *
          100,
        (getCategorizedValue("strong_loser", "gross_profit", false) /
          getCategorizedValue("strong_loser", "hands")) *
          100,
        (getCategorizedValue("unknown_loser", "gross_profit", false) /
          getCategorizedValue("unknown_loser", "hands")) *
          100,
        "",
        (getCategorizedValue("weak_winner", "gross_profit", false) /
          getCategorizedValue("weak_winner", "hands")) *
          100,
        (getCategorizedValue("middle_winner", "gross_profit", false) /
          getCategorizedValue("middle_winner", "hands")) *
          100,
        (getCategorizedValue("strong_winner", "gross_profit", false) /
          getCategorizedValue("strong_winner", "hands")) *
          100,
        (getCategorizedValue("unknown_winner", "gross_profit", false) /
          getCategorizedValue("unknown_winner", "hands")) *
          100,
      ],
    },
    {
      label: "Raked Winrate",
      data: [
        "",
        ((heroTotal.hero_gross_profit_bb - heroTotal.hero_rake_bb) /
          heroTotal.hero_hands_count) *
          100,
        ((opponentTotal.loserGrossProfitBB - opponentTotal.loserRakeBB) /
          opponentTotal.loserHands) *
          100,
        ((opponentTotal.winnerGrossProfitBB - opponentTotal.winnerRakeBB) /
          opponentTotal.winnerHands) *
          100,
        "",
        ((getCategorizedValue("weak_loser", "gross_profit", false) -
          getCategorizedValue("weak_loser", "rake", false)) /
          getCategorizedValue("weak_loser", "hands")) *
          100,
        ((getCategorizedValue("middle_loser", "gross_profit", false) -
          getCategorizedValue("middle_loser", "rake", false)) /
          getCategorizedValue("middle_loser", "hands")) *
          100,
        ((getCategorizedValue("strong_loser", "gross_profit", false) -
          getCategorizedValue("strong_loser", "rake", false)) /
          getCategorizedValue("strong_loser", "hands")) *
          100,
        ((getCategorizedValue("unknown_loser", "gross_profit", false) -
          getCategorizedValue("unknown_loser", "rake", false)) /
          getCategorizedValue("unknown_loser", "hands")) *
          100,
        "",
        ((getCategorizedValue("weak_winner", "gross_profit", false) -
          getCategorizedValue("weak_winner", "rake", false)) /
          getCategorizedValue("weak_winner", "hands")) *
          100,
        ((getCategorizedValue("middle_winner", "gross_profit", false) -
          getCategorizedValue("middle_winner", "rake", false)) /
          getCategorizedValue("middle_winner", "hands")) *
          100,
        ((getCategorizedValue("strong_winner", "gross_profit", false) -
          getCategorizedValue("strong_winner", "rake", false)) /
          getCategorizedValue("strong_winner", "hands")) *
          100,
        ((getCategorizedValue("unknown_winner", "gross_profit", false) -
          getCategorizedValue("unknown_winner", "rake", false)) /
          getCategorizedValue("unknown_winner", "hands")) *
          100,
      ],
    },
    {
      label: "Hands",
      data: [
        "",
        heroTotal.hero_hands_count,
        opponentTotal.loserHands,
        opponentTotal.winnerHands,
        "",
        opponentTotal.categorized.weak_loser_hands,
        opponentTotal.categorized.middle_loser_hands,
        opponentTotal.categorized.strong_loser_hands,
        opponentTotal.categorized.unknown_loser_hands,
        "",
        opponentTotal.categorized.weak_winner_hands,
        opponentTotal.categorized.middle_winner_hands,
        opponentTotal.categorized.strong_winner_hands,
        opponentTotal.categorized.unknown_winner_hands,
      ],
    },
    {
      label: "Raked Win",
      data: [
        "",
        getHeroValue("hero_gross_profit") - getHeroValue("hero_rake"),
        getOpponentValue("loserGrossProfit") - getOpponentValue("loserRake"),
        getOpponentValue("winnerGrossProfit") - getOpponentValue("winnerRake"),
        "",
        getCategorizedValue("weak_loser", "gross_profit", true) -
          getCategorizedValue("weak_loser", "rake", true),
        getCategorizedValue("middle_loser", "gross_profit", true) -
          getCategorizedValue("middle_loser", "rake", true),
        getCategorizedValue("strong_loser", "gross_profit", true) -
          getCategorizedValue("strong_loser", "rake", true),
        getCategorizedValue("unknown_loser", "gross_profit", true) -
          getCategorizedValue("unknown_loser", "rake", true),
        "",
        getCategorizedValue("weak_winner", "gross_profit", true) -
          getCategorizedValue("weak_winner", "rake", true),
        getCategorizedValue("middle_winner", "gross_profit", true) -
          getCategorizedValue("middle_winner", "rake", true),
        getCategorizedValue("strong_winner", "gross_profit", true) -
          getCategorizedValue("strong_winner", "rake", true),
        getCategorizedValue("unknown_winner", "gross_profit", true) -
          getCategorizedValue("unknown_winner", "rake", true),
      ],
    },
    {
      label: "Unraked Win",
      data: [
        "",
        getHeroValue("hero_gross_profit"),
        getOpponentValue("loserGrossProfit"),
        getOpponentValue("winnerGrossProfit"),
        "",
        getCategorizedValue("weak_loser", "gross_profit", true),
        getCategorizedValue("middle_loser", "gross_profit", true),
        getCategorizedValue("strong_loser", "gross_profit", true),
        getCategorizedValue("weak_loser", "gross_profit", true),
        "",
        getCategorizedValue("weak_winner", "gross_profit", true),
        getCategorizedValue("middle_winner", "gross_profit", true),
        getCategorizedValue("strong_winner", "gross_profit", true),
        getCategorizedValue("weak_winner", "gross_profit", true),
      ],
    },
    {
      label: "Rake",
      data: [
        "",
        getHeroValue("hero_rake"),
        getOpponentValue("loserRake"),
        getOpponentValue("winnerRake"),
        "",
        getCategorizedValue("weak_loser", "rake", true),
        getCategorizedValue("middle_loser", "rake", true),
        getCategorizedValue("strong_loser", "rake", true),
        getCategorizedValue("weak_loser", "rake", true),
        "",
        getCategorizedValue("weak_winner", "rake", true),
        getCategorizedValue("middle_winner", "rake", true),
        getCategorizedValue("strong_winner", "rake", true),
        getCategorizedValue("weak_winner", "rake", true),
      ],
    },
  ];

  rows.forEach((row) => {
    row.data = row.data.map((item) => {
      return fmt(item);
    });
  });

  return rows;
};
